<template>
  <nav
    v-if="renderNav"
    ref="navRef"
    aria-label="Primary"
    class="inverse-surface bg-unify-blue-700 text-white shadow-lg rounded-full p-2 pl-4 flex items-center gap-2 fixed top-2 left-1/2 -translate-x-1/2 z-50 w-[96%] md:w-full max-w-180 max-h-14"
  >
    <!-- Below `sm` the labelled links can't fit the fixed pill bar at 320px, so they
         collapse behind this toggle (a disclosure button, not a role="menu") and are
         revealed as a dropdown on demand — keeping the text labels rather than going
         icon-only, which is poor for cognitive access. Sits far-left, ahead of the logo. -->
    <button
      ref="menuButton"
      type="button"
      class="sm:hidden shrink-0 flex items-center justify-center w-10 h-10 rounded-full cursor-pointer transition-colors hover:bg-unify-blue-600"
      :aria-label="t('nav.menu')"
      aria-controls="primary-menu"
      :aria-expanded="mobileMenuOpen"
      data-gtm-id="nav-menu-toggle"
      @click="mobileMenuOpen = !mobileMenuOpen"
    >
      <component
        :is="mobileMenuOpen ? PhX : PhList"
        :size="24"
        aria-hidden="true"
      />
    </button>

    <!-- Home / logo -->
    <RouterLink
      :to="homeTarget"
      class="flex items-center shrink-0 w-8 h-8 rounded-full"
      data-gtm-id="nav-logo"
    >
      <!-- Functional: the logo is the home link, so the accessible name
           describes the destination rather than the image. -->
      <ImageComponent
        :src="unifyWhiteLogo"
        alt="Unify America home"
      />
    </RouterLink>

    <!-- Each link is its own tab stop; the current page is a route-driven cue (bg + bold +
         filled icon + aria-current), so it doesn't follow focus (WCAG 2.4.7 / 4.1.2).
         Inline row on `sm`+; below `sm` it becomes the dropdown revealed by the toggle. -->
    <ul
      id="primary-menu"
      class="flex items-center gap-1 max-sm:absolute max-sm:left-1 max-sm:right-1 max-sm:top-full max-sm:mt-2 max-sm:flex-col max-sm:items-stretch max-sm:rounded-2xl max-sm:bg-unify-blue-700 max-sm:p-2 max-sm:shadow-lg"
      :class="{ 'max-sm:hidden': !mobileMenuOpen }"
    >
      <li
        v-for="item in menuItems"
        :key="item.gtmId"
      >
        <RouterLink
          :to="item.to"
          class="flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors hover:bg-unify-blue-600 max-sm:w-full max-sm:py-2.5"
          :class="{ 'bg-unify-blue-500 font-bold': item.active }"
          :aria-current="item.active ? 'page' : undefined"
          :data-gtm-id="item.gtmId"
          @click="mobileMenuOpen = false"
        >
          <component
            v-if="iconFor(item.icon)"
            :is="iconFor(item.icon)"
            :size="20"
            :weight="item.active ? 'fill' : 'regular'"
            aria-hidden="true"
          />
          <span>{{ item.label }}</span>
        </RouterLink>
      </li>
    </ul>

    <!-- Account menu as a real <button>: keyboard-focusable, named, with aria-expanded tracking the popup (WCAG 2.1.1 / 4.1.2). ml-auto pins it to the far right. -->
    <div class="shrink-0 ml-auto">
      <button
        type="button"
        class="rounded-full cursor-pointer flex items-center"
        :aria-label="t('nav.accountMenu')"
        aria-haspopup="true"
        aria-controls="avatar-menu"
        :aria-expanded="avatarMenuOpen"
        data-gtm-id="nav-avatar"
        @click="menuToggle"
      >
        <Avatar
          :label="userInitial"
          class="!bg-transparent !border border-unify-yellow-600 max-w-10 max-h-10 !text-base !font-normal pointer-events-none"
          :size="avatarSize"
          shape="circle"
        />
      </button>
      <MenuComponent
        ref="showAvatarMenu"
        id="avatar-menu"
        :model="avatarMenuItems"
        :popup="true"
        @show="avatarMenuOpen = true"
        @hide="avatarMenuOpen = false"
      >
        <template #item="{ item, props }">
          <a
            :data-gtm-id="item.gtmId"
            v-bind="props.action"
          >
            {{ item.label }}
          </a>
        </template>
      </MenuComponent>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { AuthStore } from '@/stores/auth';
import { isDurableSkillsReportPilotParticipant } from '@/services/DurableSkillsReportService';
import unifyWhiteLogo from '@/assets/images/unifyAmericaLogos/UA-Big U Symbol White.png';
import { PhHouse, PhCalendarDots, PhChartBarHorizontal, PhList, PhX, PhFilmScript } from '@phosphor-icons/vue';

const t: any = inject('t');
const router = useRouter();
const authStore = AuthStore();
const showAvatarMenu = ref();
const avatarMenuOpen = ref(false);
const mobileMenuOpen = ref(false);
const navRef = ref<HTMLElement | null>(null);
const menuButton = ref<HTMLButtonElement | null>(null);
const localUser = ref(authStore.getUser);
const isDsrPilotParticipant = ref(false);
const renderNav = computed(() => router.currentRoute.value.meta.renderNav !== false);
const avatarSize = ref<'large' | 'normal' | 'small'>('large');

// Icon lookup so the phosphor component is chosen by the item's `icon` string.
const iconComponents: Record<string, unknown> = {
  house: PhHouse,
  calendar: PhCalendarDots,
  chart: PhChartBarHorizontal,
  Film: PhFilmScript,
};
const iconFor = (name: string) => iconComponents[name];

const userInitial = computed(() => {
  if (!localUser.value || !localUser.value.firstName) return 'UA';
  const firstInitial = localUser.value.firstName?.[0]?.toUpperCase() || '';
  const lastInitial = localUser.value.lastName?.[0]?.toUpperCase() || '';
  return firstInitial + lastInitial;
});

const userFullName = computed(() => {
  if (!localUser.value || !localUser.value.firstName || !localUser.value.lastName) return 'Unify America User';
  return localUser.value.firstName + ' ' + localUser.value.lastName;
});

const isEducator = computed(() => {
  return authStore.getUser && authStore.getUser.orgContactId;
});

const isStaff = computed(() => {
  return authStore.getUser && authStore.getUser.isStaff;
});

const homeTarget = computed(() =>
  isEducator.value ? { name: 'educator-hub-home' } : { name: 'participant-home' },
);

// Primary destinations: `active` is route-name-derived (route-driven cue); `to` gives real <a> links for natural tab order.
const menuItems = computed(() => {
  const currentRouteName = router.currentRoute.value.name;

  if (isStaff.value) {
    return [
      {
        label: t('nav.home'),
        icon: 'house',
        gtmId: 'nav-home',
        to: { name: 'participant-home' },
        active: currentRouteName === 'participant-home',
      },
      {
        label: t('nav.signup'),
        icon: 'calendar',
        gtmId: 'nav-staff-signup',
        to: { name: 'event-signup-view' },
        active: currentRouteName === 'event-signup-view',
      },
      {
        label: t('nav.insights'),
        icon: 'chart',
        gtmId: 'nav-staff-insights',
        to: { name: 'insights-private' },
        active: currentRouteName === 'insights-private',
      },
    ];
  } else if (isEducator.value) {
    return [
      {
        label: t('nav.home'),
        icon: 'house',
        gtmId: 'nav-educator-home',
        to: { name: 'educator-hub-home' },
        active: currentRouteName === 'educator-hub-home',
      },
      {
        label: t('nav.attendance'),
        icon: 'calendar',
        gtmId: 'nav-educator-attendance',
        to: { name: 'attendance-view' },
        active: currentRouteName === 'attendance-view',
      },
      {
        label: t('nav.insights'),
        icon: 'chart',
        gtmId: 'nav-educator-insights',
        to: { name: 'insights-private' },
        active: currentRouteName === 'insights-private',
      },
    ];
  } else {
    return [
      {
        label: t('nav.home'),
        icon: 'house',
        gtmId: 'nav-home',
        to: { name: 'participant-home' },
        active: currentRouteName === 'participant-home',
      },
      {
        label: t('nav.signup'),
        icon: 'calendar',
        gtmId: 'nav-participant-signup',
        to: { name: 'event-signup-view' },
        active: currentRouteName === 'event-signup-view',
      },
      ...(isDsrPilotParticipant.value ? [{
        label: t('nav.dsr'),
        icon: 'Film',
        gtmId: 'nav-dsr',
        to: {
          name: 'dsr-view',
        },
        active: currentRouteName === 'dsr-view',
      }] : []),
    ];
  }
});

const avatarMenuItems = computed(() => [
  {
    label: userFullName.value,
    items: [
      // Only show account button for non-educators
      ...(!isEducator.value ? [{
        label: t('buttons.account'),
        gtmId: 'nav-avatar-account',
        command: () => {
          router.push('/account');
        },
      }] : []),
      // Educators get no Account item, so this menu is the only nav surface common to both
      // audiences and is where the accessibility statement has to hang to be findable at all.
      {
        label: t('buttons.accessibility'),
        gtmId: 'nav-avatar-accessibility',
        command: () => {
          router.push('/accessibility');
        },
      },
      // Here because this menu is on every content page, and the site map is where educators
      // get their only route to /account — the item above is non-educators only.
      {
        label: t('buttons.siteMap'),
        gtmId: 'nav-avatar-site-map',
        command: () => {
          router.push({ name: 'site-map' });
        },
      },
      {
        label: t('buttons.logout'),
        gtmId: 'nav-avatar-logout',
        command: (_event: any) => {
          showAvatarMenu.value.hide();
          authStore.logout();
        },
      },
    ],
  },
]);

// Toggle the avatar menu on or off
const menuToggle = (event?: any) => {
  mobileMenuOpen.value = false; // don't leave two overlays open at once
  showAvatarMenu.value.toggle(event);
};

// Dismiss the mobile menu on navigation, Escape (returning focus to the toggle),
// or a click outside the nav.
watch(
  () => router.currentRoute.value.fullPath,
  () => {
    mobileMenuOpen.value = false;
  },
);

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && mobileMenuOpen.value) {
    mobileMenuOpen.value = false;
    menuButton.value?.focus();
  }
};

// Dismiss on pointerdown, NOT click. Opening the menu swaps the toggle icon component
// (PhList → PhX), which detaches the clicked <path> from the DOM before a `click`
// handler runs — so `contains(event.target)` on the orphaned node reads as "outside" and
// instantly re-closes the menu (only the button's outer ring, where the target is the
// button itself, escaped this). pointerdown fires before the icon swap, so the target
// is still attached and the menu state hasn't toggled yet.
const onDocumentPointerDown = (event: PointerEvent) => {
  if (mobileMenuOpen.value && navRef.value && !navRef.value.contains(event.target as Node)) {
    mobileMenuOpen.value = false;
  }
};

const checkPilotStatus = async () => {
  const userId = authStore.getUser.id;
  if (userId) {
    isDsrPilotParticipant.value = await isDurableSkillsReportPilotParticipant(Number(userId));
  }
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onDocumentPointerDown);

  checkPilotStatus();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.removeEventListener('pointerdown', onDocumentPointerDown);
});

watch(
  () => authStore.getUser,
  (newUser) => {
    localUser.value = newUser;
  },
  { immediate: true },
);

onBeforeMount(() => {
  localUser.value = authStore.getUser;
});
</script>

<style scoped>
/* Aileron font for the avatar initials */
:deep(.p-avatar .p-avatar-text) {
  font-family: var(--font-family-heading) !important;
  font-weight: bold !important;
}
</style>
